import { FunctionComponent, useEffect } from 'react';
import * as d3select from 'd3-selection';
import * as d3 from 'd3';
import * as d3geo from 'd3-geo';
import { useNavigate, NavigateFunction } from 'react-router';
import { clsx } from 'clsx';
import { US_STATES } from '../constants';
import { Region } from '../types';

export type Location = {
  name: string;
  lat: number;
  lon: number;
};

const drawMap = (
  navigate: NavigateFunction,
  activeStates: Region[],
  overdueStates: Region[],
  actionableStates: Region[],
  locations: Location[],
) => {
  // Width and height of map
  const width = 1960;
  const height = 500;

  // D3 Projection
  // Marking as any to prevent typescript complaining about being null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const projection: any = d3geo
    .geoAlbersUsa()
    .translate([width / 2, height / 2]) // translate to center of screen
    .scale(900); // scale things down so see entire US

  // Define path generator
  const path = d3geo
    .geoPath() // path generator that will convert GeoJSON to SVG paths
    .projection(projection); // tell path generator to use AlbersUsa projection

  const svg = d3select
    .select('#map')
    .attr('preserveAspectRatio', 'xMinYMin meet')
    .attr('viewBox', '0 0 1960 500');

  // Clear contents to redraw from existing svg
  svg.selectAll('*').remove();

  // Load GeoJSON data
  const allStatesData = US_STATES.features;
  // Lookup which states to highlight based on name
  const activeStateNames = activeStates.map((i) => i.name);
  const overdueStateNames = overdueStates.map((i) => i.name);
  const actionableStateNames = actionableStates.map((i) => i.name);

  const northeasternStates = allStatesData.filter((d) =>
    [
      'New York',
      'Delaware',
      'New Hampshire',
      'Vermont',
      'Massachusetts',
      'Connecticut',
      'Rhode Island',
      'New Jersey',
      'Maryland',
    ].includes(d.properties.name),
  );

  const tooltip = d3select.select('#map-tooltip');

  tooltip.selectAll('*').remove();
  tooltip
    .append('svg')
    .attr('class', 'absolute left-1/2 top-full -translate-x-1/2')
    .attr('width', 16)
    .attr('height', 16)
    .attr('viewBox', '0 0 8 8')
    .append('path')
    .attr('d', 'M0 0 L4 4 L8 0');

  const tooltipTitle = tooltip.append('div').attr('class', 'font-bold');
  const tooltipStatus = tooltip.append('div');

  const hideTooltip = () => {
    tooltip
      .attr('aria-hidden', 'true')
      .transition()
      .duration(200)
      .style('opacity', 0);
  };

  d3.select(document).on('keydown', function (event) {
    if (event.key === 'Escape') {
      hideTooltip();
    }
  });

  const drawStates = (
    data: typeof US_STATES.features,
    transform: string = '',
  ) => {
    return (
      svg
        .append('g')
        .attr('transform', transform)
        .selectAll('path')
        .data(data)
        .enter()
        .append('g')
        .attr('tabindex', '0')
        .attr('role', 'link')
        .attr('class', clsx('group cursor-pointer outline-none'))
        .on('click', (_event, d) => {
          const region = activeStates.find((i) => i.name === d.properties.name);

          if (region) {
            navigate(`/locations/usa/${region.id.toLowerCase()}`);
          }
        })
        .on('mouseover focus', function (_event, d) {
          const bbox = this.getBoundingClientRect();
          const isActive = activeStateNames.includes(d.properties.name);
          const isOverdue = overdueStateNames.includes(d.properties.name);
          const isActionable = actionableStateNames.includes(d.properties.name);

          tooltip
            .attr('aria-hidden', 'false')
            .style('left', `${bbox.x + bbox.width / 2}px`)
            .style('top', `${bbox.y}px`)
            .transition()
            .duration(200)
            .style('opacity', 1);

          tooltipTitle.text(d.properties.name);

          tooltipStatus.text(
            isOverdue
              ? 'Overdue'
              : isActionable
                ? 'Action required'
                : isActive
                  ? 'Up to date'
                  : null,
          );
        })
        .on('mouseout blur', function () {
          hideTooltip();
        })
        .append('path')
        // This needs to be `any` because the `data` type doesn't match
        // the path type. https://stackoverflow.com/questions/33108209/compilation-errors-when-drawing-a-piechart-using-d3-js-and-typescript
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .attr('d', path as any)
        .style('stroke', '#B0D3D4')
        .attr('vector-effect', 'non-scaling-stroke')
        .style('stroke-width', 1)
        .attr('class', (d) => {
          const isActive = activeStateNames.includes(d.properties.name);
          const isOverdue = overdueStateNames.includes(d.properties.name);
          const isActionable = actionableStateNames.includes(d.properties.name);

          return clsx(
            'relative isolate cursor-pointer fill-current outline-none',
            isOverdue &&
              'text-rose-700 hover:text-rose-900 group-focus:text-rose-900',
            isActionable &&
              !isOverdue &&
              'text-teal-700 hover:text-teal-800 group-focus:text-teal-800',
            isActive &&
              !isOverdue &&
              !isActionable &&
              'text-teal-350 hover:text-teal-550 group-focus:text-teal-550',
            !isActive && 'text-teal-100',
          );
        })
    );
  };

  drawStates(allStatesData, 'translate(-250, 0)');

  svg
    .selectAll('circle')
    .data(locations)
    .enter()
    .append('circle')
    .attr('cx', (d) => {
      return projection([d.lon, d.lat])[0];
    })
    .attr('cy', (d) => {
      return projection([d.lon, d.lat])[1];
    })
    .attr('r', 8)
    .style('fill', 'rgb(251, 191, 36)')
    .style('opacity', 0.75);

  // Line to connect the northeastern states breakout map
  svg
    .append('line')
    .attr('x1', 1056)
    .attr('y1', 100)
    .attr('x2', 1170)
    .attr('y2', 25)
    .attr('stroke', '#E5E7EB')
    .attr('stroke-width', 1);

  // Line to connect the northeastern states breakout map
  svg
    .append('line')
    .attr('x1', 1056)
    .attr('y1', 262)
    .attr('x2', 1170)
    .attr('y2', 475)
    .attr('stroke', '#E5E7EB')
    .attr('stroke-width', 1);

  // Box to highlight the northeastern states in main map
  svg
    .append('rect')
    .attr('x', 910)
    .attr('y', 100)
    .attr('width', 146)
    .attr('height', 162)
    .attr('fill', 'none')
    .attr('stroke', '#E5E7EB')
    .attr('stroke-width', 1)
    .attr('rx', 4)
    .attr('vector-effect', 'non-scaling-stroke');

  // Containing box for the northeastern states breakout map
  svg
    .append('rect')
    .attr('x', 1170)
    .attr('y', 25)
    .attr('width', 405)
    .attr('height', 450)
    .attr('fill', '#F4FBF9')
    .attr('stroke', '#E5E7EB')
    .attr('stroke-width', 1)
    .attr('rx', 4)
    .attr('vector-effect', 'non-scaling-stroke');

  drawStates(northeasternStates, 'translate(-2050, -250) scale(2.78)');
};

type USMapProps = {
  locations: Location[];
  activeStates: Region[];
  overdueStates: Region[];
  actionableStates: Region[];
  fixed?: boolean;
  className?: string;
};

export const USMap: FunctionComponent<USMapProps> = ({
  activeStates,
  overdueStates,
  actionableStates,
  locations,
  fixed,
  className,
}) => {
  const navigate = useNavigate();
  // Draw thee map once the svg element is rendered
  useEffect(() => {
    drawMap(navigate, activeStates, overdueStates, actionableStates, locations);
  });

  return (
    <>
      <div
        id="map-tooltip"
        className="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-full rounded bg-black px-4 py-3 text-sm text-white"
        role="tooltip"
        aria-hidden="true"
        style={{ opacity: 0 }}
      />

      <svg
        id="map"
        className={clsx(
          {
            fixed,
          },
          className,
        )}
      />
    </>
  );
};
